import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingSmall, LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {staticUrl} from "@khan/urls";

import {actualMinutes, actualYears, approxMinutes} from "./hero-constants.ts";

const titleText = (() => <Trans>Did you know?</Trans>)();
const regardlessText = (() => (
    <Trans>
        Regardless of who you are, mastering even just one more skill on Khan
        Academy results in learning gains.
    </Trans>
))();
const timeSpentText = (() => (
    <Trans>
        Learners have spent {actualMinutes} billion minutes learning on Khan
        Academy. {approxMinutes} billion minutes is equivalent to {actualYears}{" "}
        years.
    </Trans>
))();

export const TeacherHeroFigure = (): React.ReactNode => (
    <HeroFigure
        imagePath={staticUrl("/images/new_registration/teacher-hero.png")}
        title={titleText}
        text={regardlessText}
    />
);

export const RolePageHeroFigure = (): React.ReactNode => (
    <HeroFigure
        imagePath={staticUrl("/images/new_registration/role-hero.png")}
        title={titleText}
        text={regardlessText}
    />
);

export const ParentHeroFigure = (): React.ReactNode => (
    <HeroFigure
        imagePath="/images/new_registration/parent-hero.png"
        title={titleText}
        text={timeSpentText}
    />
);

export const LogInPageHeroFigure = (): React.ReactNode => (
    <HeroFigure
        imagePath={staticUrl("/images/new_registration/log-in-hero@2x.png")}
        title={titleText}
        text={regardlessText}
    />
);

export const LearnerHeroFigure = (): React.ReactNode => (
    <HeroFigure
        imagePath={staticUrl("/images/new_registration/learner-hero.png")}
        title={titleText}
        text={timeSpentText}
    />
);

export const LearnerUnderageHeroFigure = (): React.ReactNode => (
    <HeroFigure
        imagePath={staticUrl(
            "/images/new_registration/learner-underage-hero.png",
        )}
        title={titleText}
        text={timeSpentText}
    />
);

interface HeroFigureProps {
    /**
     * The path to the image to display.
     */
    imagePath: string;
    /**
     * The small heading to display beneath the image.
     */
    title: string | React.ReactNode;
    /**
     * The body text to display beneath the title.
     */
    text: string | React.ReactNode;
    /**
     * The maximum width of the entire figure. Defaults to 480px
     */
    maxWidth?: number;
    /**
     * Alt text for the image. Defaults to a blank string since this is a decorative image.
     */
    imageAlt?: string;
}

/**
 * A figure for the registration hero section of the new signup flow.
 */
const HeroFigure = ({
    imagePath,
    title,
    text,
    maxWidth = 480,
    imageAlt = "",
}: HeroFigureProps) => {
    const styles = StyleSheet.create({
        container: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            maxWidth,
        },
    });

    return (
        <View style={styles.container}>
            <img
                src={staticUrl(imagePath)}
                alt={imageAlt}
                style={{width: "100%"}}
            />
            <Strut size={spacing.large_24} />
            <HeadingSmall>{title}</HeadingSmall>
            <Strut size={spacing.medium_16} />
            <LabelMedium>{text}</LabelMedium>
        </View>
    );
};
